import { useEffect, useState, useCallback } from 'react';
import { getCurrentUser } from '@/modules/users/redux/UserCRUD';
import { getEmployeeByUser } from '@/modules/employees/redux/EmployeeCRUD';
import { UserModel } from '@/modules/users/models/UserModel';



// Use the existing UserModel instead of creating a new interface
type CurrentUser = UserModel;

interface Employee {
  id: string;
  name: string;
  code: string;
  status: string;
  company?: {
    id: string;
    name: string;
    code: string;
  };
  department?: {
    id: string;
    name: string;
    code: string;
  };
  user: {
    id: string;
    email: string;
  };
}

export const useCurrentUserCompany = () => {
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCurrentUserData = async () => {
      try {
        setLoading(true);
        
        // Fetch current user
        const userResponse = await getCurrentUser();
        setCurrentUser(userResponse.data);
        
        // Try to fetch employee data for this user
        try {
          const employeeResponse = await getEmployeeByUser(userResponse.data.id);
          setCurrentEmployee(employeeResponse.data);
        } catch (employeeErr) {
          // This is not an error - user might not be an employee
          // Only log if it's not a 404 (expected when user is not an employee)
          if ((employeeErr as any)?.response?.status !== 404) {
            console.warn('Unexpected error fetching employee data:', employeeErr);
          }
        }
        
        setError(null);
      } catch (err) {
        setError('Không thể lấy thông tin người dùng hiện tại');
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUserData();
  }, []);

  const getPrimaryCompany = useCallback((): string | null => {
    // First priority: Employee's company
    if (currentEmployee?.company?.id) {
      return currentEmployee.company.id;
    }
    
    return null;
  }, [currentEmployee]);

  const getAllUserCompanies = useCallback((): string[] => {
    const companies: string[] = [];
    
    // Add employee's company if exists
    if (currentEmployee?.company?.id) {
      companies.push(currentEmployee.company.id);
    }
    
    // Note: UserModel doesn't have company info in roles
    // If we need company info from roles, we'd need to fetch it separately
    
    return companies;
  }, [currentEmployee]);

  return {
    currentUser,
    currentEmployee,
    loading,
    error,
    getPrimaryCompany: getPrimaryCompany,
    getAllUserCompanies: getAllUserCompanies,
  };
};
